import { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { api } from '../api/axios'

interface InscritoInfo {
  nome_atirador: string
}

interface ModalidadeInscritos {
  idmodalidade: number
  nome: string
  total_inscritos: number
  inscritos: InscritoInfo[]
}

interface InscritosResponse {
  total: number
  modalidades: ModalidadeInscritos[]
}

interface Campeonato {
  idcampeonato: number
  descricao: string
  temporada: number
  clube_nome: string
}

export function InscritosPublicoPage() {
  const [searchParams] = useSearchParams()
  const [campeonatos, setCampeonatos] = useState<Campeonato[]>([])
  const [idcampeonato, setIdcampeonato] = useState<number | null>(null)
  const [data, setData] = useState<InscritosResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [expandidos, setExpandidos] = useState<Record<number, boolean>>({})

  useEffect(() => {
    api.get<{ data: Campeonato[] }>('/publico/campeonatos')
      .then(r => {
        const camps = r.data.data
        setCampeonatos(camps)
        const paramId = searchParams.get('idcampeonato')
        if (paramId) {
          setIdcampeonato(parseInt(paramId, 10))
        } else if (camps.length > 0) {
          setIdcampeonato(camps[0].idcampeonato)
        }
      })
      .catch(() => setError('Erro ao carregar campeonatos.'))
  }, [searchParams])

  useEffect(() => {
    if (!idcampeonato) return
    setLoading(true)
    setError(null)
    setExpandidos({})
    api.get<InscritosResponse>(`/publico/inscritos?idcampeonato=${idcampeonato}`)
      .then(r => setData(r.data))
      .catch(() => setError('Erro ao carregar inscritos. Tente novamente.'))
      .finally(() => setLoading(false))
  }, [idcampeonato])

  function toggleModalidade(id: number) {
    setExpandidos(prev => ({ ...prev, [id]: !prev[id] }))
  }

  const campNome = campeonatos.find(c => c.idcampeonato === idcampeonato)?.descricao ?? ''

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 40%, #16213e 70%, #0f3460 100%)' }}>
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img
            src="/sponsors/logo-aguia.jpeg"
            alt="Águia"
            className="w-10 h-10 rounded-full object-cover border-2 border-yellow-500"
            onError={e => { (e.target as HTMLImageElement).style.display = 'none' }}
          />
          <div>
            <p className="text-white font-bold text-sm">Campeonato 2026 Águia</p>
            <p className="text-compumaq-textoSuave text-xs">30/05/2026 - 31/05/2026</p>
          </div>
        </Link>
        <Link to="/" className="text-sm text-compumaq-textoSuave hover:text-white border border-white/20 px-3 py-1.5 rounded-lg transition-colors">
          &larr; Voltar
        </Link>
      </header>

      {/* Content */}
      <div className="flex-1 px-4 py-8 max-w-3xl mx-auto w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-black text-white mb-2">Inscritos</h1>
          <p className="text-compumaq-textoSuave text-sm">Atiradores inscritos por modalidade</p>
        </div>

        {/* Selector de campeonato */}
        {campeonatos.length > 1 && (
          <div className="mb-6">
            <select
              value={idcampeonato ?? ''}
              onChange={e => setIdcampeonato(parseInt(e.target.value, 10))}
              className="w-full bg-black/30 border border-white/20 text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
            >
              {campeonatos.map(c => (
                <option key={c.idcampeonato} value={c.idcampeonato}>{c.descricao} — {c.temporada}</option>
              ))}
            </select>
          </div>
        )}

        {error && (
          <div className="mb-4 bg-red-900/40 border border-red-500/50 rounded-xl p-4 text-red-300 text-sm">
            {error}
          </div>
        )}

        {loading && (
          <div className="text-center py-16 text-compumaq-textoSuave">Carregando...</div>
        )}

        {!loading && data && (
          <>
            {/* Total geral */}
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-2xl p-5 mb-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center shrink-0">
                <svg className="w-6 h-6 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div>
                <p className="text-yellow-400 font-bold text-sm uppercase tracking-wider">{campNome}</p>
                <p className="text-white text-3xl font-black">{data.total} inscritos</p>
                <p className="text-compumaq-textoSuave text-xs mt-0.5">{data.modalidades.length} modalidade{data.modalidades.length !== 1 ? 's' : ''}</p>
              </div>
            </div>

            {/* Lista de modalidades */}
            {data.modalidades.length === 0 ? (
              <div className="text-center py-12 text-compumaq-textoSuave">
                <p>Nenhuma inscricao registrada ainda.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {data.modalidades.map(mod => (
                  <div
                    key={mod.idmodalidade}
                    className="bg-compumaq-pretoSuave/5 border border-white/10 rounded-2xl overflow-hidden"
                  >
                    {/* Header da modalidade */}
                    <button
                      className="w-full flex items-center justify-between px-5 py-4 hover:bg-compumaq-pretoSuave/5 transition-colors"
                      onClick={() => toggleModalidade(mod.idmodalidade)}
                    >
                      <div className="text-left">
                        <p className="text-white font-semibold">{mod.nome}</p>
                        <p className="text-compumaq-textoSuave text-xs mt-0.5">{mod.total_inscritos} atirador{mod.total_inscritos !== 1 ? 'es' : ''}</p>
                      </div>
                      <div className="flex items-center gap-3 shrink-0 ml-4">
                        <span className="bg-yellow-500/20 text-yellow-400 font-bold text-sm px-3 py-1 rounded-full">
                          {mod.total_inscritos}
                        </span>
                        <svg
                          className={`w-4 h-4 text-compumaq-textoSuave transition-transform duration-200 ${expandidos[mod.idmodalidade] ? 'rotate-180' : ''}`}
                          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </button>

                    {/* Lista expandida */}
                    {expandidos[mod.idmodalidade] && (
                      <div className="border-t border-white/10 px-5 py-3">
                        {mod.inscritos.length === 0 ? (
                          <p className="text-compumaq-textoSuave text-sm py-2">Nenhum atirador.</p>
                        ) : (
                          <ol className="space-y-1">
                            {mod.inscritos.map((insc, idx) => (
                              <li key={idx} className="flex items-center gap-3 py-1.5 border-b border-white/5 last:border-0">
                                <span className="text-compumaq-textoSuave text-xs w-5 shrink-0 text-right">{idx + 1}.</span>
                                <span className="text-compumaq-textoSuave text-sm">{insc.nome_atirador}</span>
                              </li>
                            ))}
                          </ol>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
